// 64-bit attr/cmd ids — copied VERBATIM from common/04 §4 (via specs/21 §3/§5/§6).
// Hard rule (spec 21 §0): never invent ids; unknown clusters are hidden.

import i18n from '../../../i18n';
import type { SignalFrame } from '../../../lib/ws';

// ---- clusters (common/04 §4.0) ----
export const CL_HEALTH = '0x8000';
export const CL_SENSOR = '0x8010';
export const CL_MOTION = '0x8020';
export const CL_DOORBELL = '0x8030';
export const CL_OBJECT = '0x8041';
export const CL_SOUND = '0x8042';
export const CL_SOUNDRECOG = '0x8043';
export const CL_TAMPER = '0x8044';
export const CL_RECORDING = '0x8050';
export const CL_PTZ = '0x8060';
export const CL_IMAGE = '0x8070';
export const CL_LIGHT = '0x8071';
export const CL_FLOODLIGHT = '0x8072';
export const CL_AUDIO = '0x8080';
export const CL_SIREN = '0x8081';
export const CL_SDCARD = '0x8090';
export const CL_STREAMING = '0x80A0';
export const CL_PLUG = '0x8001';
export const CL_VIDEO_LOSS = '0x80f0';
export const CL_WIFI = '0x80c0'; // BLE 配對 / Wi-Fi 配網（device/09；Wi-Fi 換網 §5.4）

// ---- NVR/DVR per-channel input status (ADR-045 / common 04 §4.17C) ----
export const CHANNEL_VIDEO_LOSS_ENABLED = '0x21000080F0000000';
export const CHANNEL_VIDEO_SIGNAL_PRESENT = '0x21000080F0000100';
export const CHANNEL_SIGNAL_FORMAT = '0x21000080F0000200';

// ---- Detection (spec 21 §3.1) ----
export const MOTION_ENABLED = '0x2100008020000000';
export const MOTION_SENSITIVITY = '0x2100008020000100'; // optional member → probe
// APP#45 偵測區域(common!96 / capdict v1.8.0):ZoneEnabled 區域限定總開關、DetectionZones raw ROI。
export const MOTION_ZONE_ENABLED = '0x2100008020000200'; // SET bool op:set — false=全畫面偵測
export const MOTION_DETECTION_ZONES = '0x2100008020000300'; // SET raw op:set — rawVal region JSON(見 zones.ts)
export const MOTION_DETECTION_SCHEDULE = '0x2100008020000400'; // SET raw WeeklySchedule v1 (ADR-054)
export const MOTION_ALARM_INTERVAL = '0x2100008020000500'; // SET intVal seconds:180/300/600/1800
// 情境偵測（ls-loop 字典 1.20.0+loop.2，參考 Alfred）：SET intVal 秒數，0 = 關、10–3600；只推播、不錄影
export const MOTION_STOPPED_SEC = '0x2100008020000700';
export const PERSON_LINGER_SEC = '0x2100008041000200';
export const PERSON_ABSENT_SEC = '0x2100008041000300';
export const OBJECT_ENABLED = '0x2100008041000000';
export const OBJECT_CONFIDENCE = '0x2100008041000100'; // optional member → probe
export const SOUND_ENABLED = '0x2100008042000000';
export const SOUND_LEVEL = '0x2100008042000100';
export const SOUND_CURRENT_LEVEL = '0x2100008042000200'; // optional member → probe
export const SOUNDRECOG_ENABLED = '0x2100008043000000';
export const TAMPER_ENABLED = '0x2100008044000000';

// ---- Image (spec 21 §3.3) ----
export const IMAGE_FLIP = '0x2100008070000000';
export const IMAGE_WATERMARK = '0x2100008070000100';
export const IMAGE_SLEEP = '0x2100008070000200';
export const IMAGE_AI_OVERLAY = '0x2100008070000300'; // CAM-5 AiOverlay 偵測框顯示（capability-gated）

// ---- Light / NightMode (spec 21 §3.4) ----
export const LIGHT_STATUS = '0x2100008071000000';
export const LIGHT_NIGHTMODE = '0x2100008071000100'; // enum 0/1 (common/04 §4.10: no auto)
export const LIGHT_WARNING = '0x2100008071000200';

// ---- Floodlight / Bulb (cluster 0x8072, common/04 §4;APP#40) ----
export const FLOOD_SWITCH = '0x2100008072000000'; // bool
export const FLOOD_BRIGHTNESS = '0x2100008072000100'; // int 0-100
export const FLOOD_COLOR_TEMP = '0x2100008072000200'; // int 2200-5000 K
export const FLOOD_LIGHT_WARN = '0x2100008072000300'; // bool 偵測聯動亮燈

// ---- Audio 喇叭/麥克風 (cluster 0x8080;APP#40) ----
export const AUDIO_SPEAKER_VOL = '0x2100008080000000'; // int 0-100
export const AUDIO_MIC_VOL = '0x2100008080000100'; // int 0-100
export const AUDIO_MUTE_SPEAKER = '0x2100008080000200'; // bool

// ---- Siren / Alarm (cluster 0x8081;APP#40) ----
export const SIREN_ALARM = '0x2100008081000000'; // bool 警報總開關
export const SIREN_VOLUME = '0x2100008081000100'; // int 0-100
export const SIREN_DURATION = '0x2100008081000200'; // int 5-120 s
export const SIREN_TRIGGER = '0x3100008081000000'; // CMD 手動觸發

// ---- Doorbell / Chime (cluster 0x8030;APP#40) ----
export const DOORBELL_CHIME_TYPE = '0x2100008030000000'; // enum 0 機械 / 1 電子
export const DOORBELL_CHIME_TIME = '0x2100008030000100'; // int 0-120

// ---- Recording (spec 21 §6.1; ADR-046 三業務欄位：LS app 走 ws op:set) ----
// 三業務欄位 attr id（common/04 §4）：LS 自家 app 以 ws op:set 寫入（enumVal，見下方 map），
// restful REST PUT 保留給 3rd-party。裝置在值有變的 op:set 成功後會主動廣播「屬性變更回報」
// (op:report，無 request_id，id = 同一 attr) — 見 ADR-046 / common/04 §5.11 / signaling 02 §6.11。
// App 收到本裝置這三 id 的回報即 refetch 收斂顯示。
export const REC_MODE = '0x2100008050000000';
export const REC_LOCATION = '0x2100008050000100';
export const REC_QUALITY = '0x2100008050000200';
export const REC_SCHEDULE = '0x2100008050000500'; // SET raw WeeklySchedule v1; NVR/DVR top-level channel
/** 四個 cloud-authoritative「錄影設定」attr id 的集合，供屬性變更回報比對用。 */
export const REC_SETTING_IDS = [REC_MODE, REC_LOCATION, REC_QUALITY, REC_SCHEDULE] as const;

// 錄影設定 enum 值集（須對齊 dictionary / signaling recsettings.go；op:set 送 enumVal:int）：
//   RecordMode:     none=0 / continuous=1 / event=2
//   RecordLocation: cloud=0 / sdcard=1
//   RecordQuality:  720p=0 / 1080p=1 / 2K=2 / 4K=3（大小寫 2k/4k 皆接受）
export const REC_MODE_ENUM: Record<string, number> = { none: 0, continuous: 1, event: 2 };
export const REC_LOCATION_ENUM: Record<string, number> = { cloud: 0, sdcard: 1 };
export const REC_QUALITY_ENUM: Record<string, number> = {
  '720p': 0,
  '1080p': 1,
  '2k': 2,
  '4k': 3,
};
/** 畫質字串 → enum int，容忍 2K/4K 大小寫（統一小寫查表）。 */
export function recQualityEnum(v: string): number | undefined {
  return REC_QUALITY_ENUM[v.toLowerCase()];
}

/**
 * 從廣播 frame（op:report 屬性變更回報，或 type:event）取出 attr/event id。
 * 回報 id 落在 top-level `id`（common/04 op model），退回 `event_id` / payload
 * （與 PtzSection 的取值慣例一致）。
 */
export function frameAttrId(frame: SignalFrame): string | undefined {
  const payload = frame.payload as { id?: unknown; event_id?: unknown } | undefined;
  const raw = frame.id ?? frame.event_id ?? payload?.id ?? payload?.event_id;
  return typeof raw === 'string' ? raw : undefined;
}

/** 此 frame 是否為四個錄影設定 attr 之一的變更回報 / 事件。 */
export function isRecordSettingReport(frame: SignalFrame): boolean {
  const id = frameAttrId(frame);
  return id != null && (REC_SETTING_IDS as readonly string[]).includes(id);
}
export const REC_STATUS = '0x2100008050000300'; // STAT readonly — WSS ok
export const REC_MANUAL_LENGTH = '0x2100008050000400'; // pure device knob — WSS ok
export const REC_MANUAL_START = '0x3100008050000000'; // CMD 手動啟動錄影（in-live 手動事件快捷；APP#41）

// ---- SDCARD (spec 21 §3.10) ----
export const SD_PRESENT = '0x2100008090000000';
export const SD_STATUS = '0x2100008090000100'; // 1 normal/2 abnormal/3 no-space/4 formatting/5 no-sd
export const SD_CAPACITY = '0x2100008090000200'; // STAT str
export const SD_FORMAT = '0x3100008090000000'; // CMD destructive — double confirm
export const SD_UNMOUNT = '0x3100008090000100'; // CMD — single confirm

// ---- PTZ / 追蹤 (spec 21 §3.8, cluster 0x8060) ----
// 共用真源在 ../ptz.ts(Live 方向盤與設定頁同一組 id);此處 re-export 供設定頁。
export {
  PTZ_OBJECT_TRACKING,
  PTZ_ZOOM,
  PTZ_MOVE_RELATIVE,
  PTZ_MOVE_ABSOLUTE,
  PTZ_STOP,
  PTZ_CALIBRATE,
  PTZ_EVT_MOVE_STOP,
} from '../ptz';

// ---- Device Health (spec 21 §5) ----
export const HEALTH_ONLINE = '0x2100008000000000';
export const HEALTH_FW_VERSION = '0x2100008000000100';
export const HEALTH_WIFI = '0x2100008000000200';
export const HEALTH_IP = '0x2100008000000300';
export const HEALTH_TEMP = '0x2100008000000400';
export const HEALTH_TIMEZONE = '0x2100008000000500'; // SET str — device timezone POSIX (common/22; ≠ WeeklySchedule IANA)
export const HEALTH_REBOOT = '0x3100008000000000'; // CMD — single confirm
export const HEALTH_FACTORY_RESET = '0x3100008000000100'; // CMD destructive — double confirm
export const HEALTH_OTA_START = '0x3100008000000200'; // CMD(strVal=target_version) — LS App 走 Signal
// APP#45 保留-wifi 重置(common!96 / capdict v1.8.0):回復設定但保留 Wi-Fi/身分/綁定/連線,不重開機不解綁。
export const HEALTH_RESET_KEEP_WIFI = '0x3100008000000300'; // CMD 半破壞性 — double confirm

// ---- Wi-Fi 重配 (device/09 §8.1; cluster 0x80C0; 保綁定) ----
// 模式 A 走 WSS op:set + 0x31 CMD id + rawVal(見 ops.ts opWifiScan/opWifiReconfigure)。
// 授權 owner-only(字典 write_authz:owner)。WIFI_SCAN 為新成員、字典待登錄(REST-A)。
export const WIFI_RECONFIGURE = '0x31000080C0000100'; // CMD raw {ssid,psk,[bssid],[security]} — 換 Wi-Fi
export const WIFI_ENTER_RECONFIG_BLE = '0x31000080C0000200'; // CMD — 啟 BLE 廣播(需 BLE 機種; web 不適用)
export const WIFI_SCAN = '0x31000080C0000300'; // CMD — device 掃描回報可見網路; report.rawVal={aps:[...]}
export const WIFI_SSID = '0x21000080C0000100'; // STAT str — 目前連線 SSID
export const WIFI_EVT_SUCCEEDED = '0x11000080C0000100'; // EVT — wifi.reconfig.succeeded {ssid,at}
export const WIFI_EVT_FAILED = '0x11000080C0000200'; // EVT — wifi.reconfig.failed {reason,rolled_back,at}

// ---- Plug 插座 (cluster 0x8001, spec 21 §3.11;APP#39 — GAP-DS-2 已由 dictionary v1.6.0 補齊) ----
export const PLUG_OUTLET_ON = '0x2100008001000000'; // SET bool 通斷
export const PLUG_ACTIVE_POWER = '0x2100008001000100'; // STAT int 0.1 W/LSB
export const PLUG_VOLTAGE = '0x2100008001000200'; // STAT int 0.1 V/LSB
export const PLUG_CURRENT = '0x2100008001000300'; // STAT int 1 mA/LSB
export const PLUG_ENERGY = '0x2100008001000400'; // STAT int 1 Wh/LSB(÷1000=kWh)

// ---- Sensor 環境感測 (cluster 0x8010, spec 21 §3.9;APP#39) ----
export const SENSOR_TEMPERATURE = '0x2100008010000000'; // STAT int 溫度
export const SENSOR_HUMIDITY = '0x2100008010000100'; // STAT int 濕度
export const SENSOR_TEMP_UPPER = '0x2100008010000200'; // SET int 溫度上限
export const SENSOR_TEMP_LOWER = '0x2100008010000300'; // SET int 溫度下限
// 0.01/LSB（common/04 §4.7，字典 1.20.0+loop.4）：溫濕度與四個上下限都是 ×100 整數（2350 = 23.50 °C）
export const SENSOR_HUM_UPPER = '0x2100008010000400'; // SET int 濕度上限 0..10000
export const SENSOR_HUM_LOWER = '0x2100008010000500'; // SET int 濕度下限 0..10000
/** 0.01/LSB → 顯示：2350 → "23.5"、2300 → "23"（小數一位、整數不帶 .0）。 */
export function centi(v: number): string {
  const tenths = Math.round(v / 10);
  return tenths % 10 === 0 ? String(tenths / 10) : (tenths / 10).toFixed(1);
}
// ⚠️ 濕度上下限 attr 不存在(GAP-DS-3)→ 濕度唯讀、不渲染閾值 widget。

// sub-chip label maps (advertise-only chips; spec 21 §3.1/§2.3)
// ⚠️ i18n: 顯示名須在使用點求值(語言切換要即時反映),故包成 function 而非
// 模組頂層 const(後者會在 i18n init 前 / 語言切換時凍結舊字串)。
export function motionSubLabels(): Record<string, string> {
  return {
    '0x01': i18n.t('deviceSettings.motionSubPir'),
    '0x02': i18n.t('deviceSettings.motionSubVideo'),
  };
}
export function objectSubLabels(): Record<string, string> {
  return {
    '0x01': i18n.t('deviceSettings.objectSubPerson'),
    '0x02': i18n.t('deviceSettings.objectSubVehicle'),
    '0x03': i18n.t('deviceSettings.objectSubAnimal'),
    '0x04': i18n.t('deviceSettings.objectSubPackage'),
  };
}
export function soundRecogSubLabels(): Record<string, string> {
  return {
    '0x01': i18n.t('deviceSettings.soundRecogSubT3'),
    '0x02': i18n.t('deviceSettings.soundRecogSubT4'),
    '0x03': i18n.t('deviceSettings.soundRecogSubGlass'),
    '0x04': i18n.t('deviceSettings.soundRecogSubDog'),
    '0x05': i18n.t('deviceSettings.soundRecogSubGunshot'),
  };
}

// ---- MyCam 手機攝影機（ls-loop 字典 1.20.0+loop.1；docs/mycam）----
export const CAPTURE_STATE = '0x2100008000000600';   // STAT composite {state: active/paused, reason}
export const REQUEST_REPORT = '0x3100008000000400';  // CMD 無值，owner：請攝影機送回報問題包（F15）
export const BATTERY_LEVEL = '0x21000080D0000000';   // STAT int %
export const EXTERNAL_POWER = '0x21000080D0000200';  // STAT bool 接著電源
export const UNPLUG_ALERT = '0x21000080D0000900';    // SET bool，owner：拔電立即通知（F22）
